import React, { useContext, useState } from "react";
import styled from "styled-components";

import {
  Modal as BaseModal,
  Button,
  VSpace,
  FlexBox,
  Text,
  Toolbar,
} from "@examspark/common-ui";
import {
  IconPageNextLine,
  IconPagePreviousLine,
} from "@examspark/common-ui/dist/components/icons";
import SearchField from "@examspark/common-ui/dist/components/inputs/SearchField";
import {
  SkipContentButton,
  SkipContentProvider,
  useSkipContent,
} from "@examspark/common-ui/dist/components/utils/SkipContent";
import { useDebounce } from "@examspark/common-ui/dist/hooks/useDebounce";
import {
  ObjectType,
  SearchableResourceSortableFields,
  SearchableSortDirection,
} from "@examspark/types";

import { WindowContext } from "../common/AppWrapper";
import Copyright from "../common/Copyright";
import Modal from "../common/Modal";
import { Footer } from "../common/PageLayout";

import BrowseList from "./BrowseList";
import FilterBank from "./filters/FilterBank";
import FilterCoursesByTag from "./filters/FilterCoursesByTag";
import FilterGrade from "./filters/FilterGrade";
import FilterLanguage from "./filters/FilterLanguage";
import FilterObjectives from "./filters/FilterObjectives";
import FilterSubject from "./filters/FilterSubject";
import FilterType from "./filters/FilterType";
import FilterUnitsByTag from "./filters/FilterUnitsByTag";
import GlossaryList from "./GlossaryList";
import { usePaginatedSearch, useSearch } from "./useSearch";

const Styled = styled.div`
  max-width: var(--app-contain-width);

  // margin: 0 auto;
  /* display: flex; */
  gap: 3rem;
  display: flex;
`;

const LayoutMain = styled.main`
  flex: 1;
`;

const LayoutFilters = styled.nav`
  width: clamp(23rem, 25vw, 30rem) 1fr;
`;

const Filters = styled(VSpace)`
  gap: 3rem;
`;
export const Filter = styled(VSpace)`
  gap: 1rem;
`;

const Main = styled(VSpace)`
  gap: 2rem;
`;

const Search = styled.div`
  display: flex;
  gap: 1rem;
`;

const SearchSearchField = styled.div`
  flex: 1;
`;
const SearchFilter = styled.div``;

const PagerBar = styled(FlexBox)`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const SkipFilters = ({ children }: { children: React.ReactNode }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  useSkipContent("Skip to Browse", ref);

  return (
    <div ref={ref} style={{ display: "contents" }}>
      {children}
    </div>
  );
};

export interface FilterData {
  subject?: string;
  grade?: string[];
  type?: ObjectType[];
  objectiveScope?: string;
  objectiveIds?: string[];
  bankId?: string[];
  languages?: string[];
  tags?: Record<string, string[]>;
  query?: string;
}

export const serializeBrowseFilter = (filter: FilterData) => {
  return btoa(JSON.stringify(filter));
};

type BrowseProps = {
  // todo: add a real contract here
  resultList: typeof BrowseList | typeof GlossaryList;
  searchQuery?: "Search" | "SearchVocabulary";
  withFilters: {
    bank?: boolean;
    subject?: boolean;
    grade?: boolean;
    unitByTag?: boolean;
    courseByTag?: boolean;
    language?: boolean;
    type?: boolean;
    query?: boolean;
    objectives?: boolean;
  };
  useSearchFn?: typeof useSearch;
  searchFilter: FilterData;
  setSearchFilter: (filter: FilterData) => void;
  onFilterClear?: () => void;
  pageTitle?: string;

  toolbar?: React.ReactNode;
};
const Browse = (props: BrowseProps) => {
  const { searchFilter, setSearchFilter } = props;
  const searchRef = React.useRef<HTMLInputElement>(null);

  const debouncedSearchFilter = useDebounce(searchFilter, 500);

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const win = useContext(WindowContext);
  const {
    data,
    isFetching,
    hasNextPage,
    fetchNextPage,
    hasPreviousPage,
    fetchPreviousPage,
    currentPage,
    totalPages,
    refetch,
  } = usePaginatedSearch(
    {
      queryKey: [debouncedSearchFilter],
      filter: debouncedSearchFilter,
      searchLabel: props.searchQuery ?? "Search",
      sort: [
        {
          field: SearchableResourceSortableFields.Name,
          direction: SearchableSortDirection.Asc,
        },
      ],
    },
    props.useSearchFn,
  );

  const renderFilters = () => (
    <Filters>
      {props.withFilters.subject && (
        <FilterSubject
          filterKey="subject"
          filters={searchFilter}
          useSearchFn={props.useSearchFn}
          onChange={(value) =>
            setSearchFilter({ ...searchFilter, subject: value })
          }
        />
      )}
      {props.withFilters.bank && (
        <FilterBank
          filters={searchFilter}
          useSearchFn={props.useSearchFn}
          onChange={(value) => {
            setSearchFilter({ ...searchFilter, bankId: value });
          }}
        />
      )}
      {props.withFilters.courseByTag && (
        <FilterCoursesByTag
          filters={searchFilter}
          useSearchFn={props.useSearchFn}
          onChange={(values) => {
            const { "course-id": courseTags, ...otherTags } =
              searchFilter.tags ?? {};
            setSearchFilter({
              ...searchFilter,
              tags: {
                ...otherTags,
                "course-id": values,
              },
            });
          }}
        />
      )}
      {props.withFilters.unitByTag && (
        <FilterUnitsByTag
          filters={searchFilter}
          useSearchFn={props.useSearchFn}
          onChange={(values) => {
            const { "unit-id": unitTags, ...otherTags } =
              searchFilter.tags ?? {};
            setSearchFilter({
              ...searchFilter,
              tags: {
                ...otherTags,
                "unit-id": values,
              },
            });
          }}
        />
      )}
      {props.withFilters.grade && (
        <FilterGrade
          filterKey="grade"
          useSearchFn={props.useSearchFn}
          filters={searchFilter}
          onChange={(value) =>
            setSearchFilter({ ...searchFilter, grade: value })
          }
        />
      )}
      {props.withFilters.language && (
        <FilterLanguage
          filters={searchFilter}
          useSearchFn={props.useSearchFn}
          onChange={(value) => {
            setSearchFilter({ ...searchFilter, languages: value });
          }}
        />
      )}

      {props.withFilters.objectives && (
        <FilterObjectives
          filters={searchFilter}
          useSearchFn={props.useSearchFn}
          onChange={(value) => {
            setSearchFilter({ ...searchFilter, ...value });
          }}
        />
      )}
      {props.withFilters.type && (
        <FilterType
          filters={searchFilter}
          useSearchFn={props.useSearchFn}
          onChange={(value) =>
            setSearchFilter({ ...searchFilter, type: value })
          }
        />
      )}
    </Filters>
  );

  return (
    <>
      <Modal
        isOpen={isFilterModalOpen}
        onRequestClose={() => setIsFilterModalOpen(false)}
        size="small"
        buttons={
          <>
            <Button
              title="View Results"
              color="primary"
              onClick={() => setIsFilterModalOpen(false)}
            >
              View{" "}
              {(data?.total || 0) === 1
                ? `1 Result`
                : `${data?.total || 0} Results`}
            </Button>
          </>
        }
      >
        <BaseModal.PaddedBody>
          <div>{renderFilters()}</div>
        </BaseModal.PaddedBody>
      </Modal>
      <SkipContentProvider>
        <Styled>
          {(win.device === "desktop" || win.device === "tablet") && (
            <LayoutFilters>
              <SkipContentButton />
              {renderFilters()}
            </LayoutFilters>
          )}

          <LayoutMain>
            <Main>
              <Text.H1>
                {props.pageTitle ?? "Browse All Content"}{" "}
                {!!data?.total && <>({data.total})</>}
              </Text.H1>

              <Search>
                <SkipFilters>
                  <SearchSearchField>
                    <SearchField
                      value={searchFilter.query}
                      inputRef={searchRef}
                      onChange={(query) =>
                        setSearchFilter({
                          ...searchFilter,
                          query,
                        })
                      }
                    />
                  </SearchSearchField>
                </SkipFilters>
                <Toolbar>
                  <Button
                    title="Refresh"
                    onClick={() => {
                      refetch();
                    }}
                  >
                    Refresh
                  </Button>
                  <Button
                    title="Clear"
                    onClick={() => {
                      if (
                        window.confirm(
                          "Are you sure you want to reset your filters?",
                        )
                      ) {
                        // the TextInput is not behaving as a proper controlled component
                        // so we have to clear it manually
                        if (searchRef.current) {
                          searchRef.current.value = "";
                        }
                        props.onFilterClear?.();
                      }
                    }}
                  >
                    Clear
                  </Button>

                  {win.device === "phone" && (
                    <>
                      <SearchFilter>
                        <Button
                          title="Show Filters"
                          onClick={() => setIsFilterModalOpen(true)}
                          hasBadge
                          badgeCount={
                            Object.values(searchFilter).filter(
                              (v: FilterData[keyof FilterData]) =>
                                typeof v === "string" ? v : v?.length,
                            ).length
                          }
                        >
                          Filters
                        </Button>
                      </SearchFilter>
                    </>
                  )}
                  {props.toolbar}
                </Toolbar>
              </Search>
              {/* Announce the search results to the screen reader */}
              <Toolbar.Right>
                <label aria-live="assertive">
                  {isFetching
                    ? "Searching..."
                    : `
                    ${data?.total ?? 0} Results found
                  `}
                </label>
              </Toolbar.Right>
              <VSpace>
                <PagerBar>
                  <Button
                    title="previous"
                    variant="plain"
                    disabled={!hasPreviousPage || isFetching}
                    onClick={fetchPreviousPage}
                  >
                    <IconPagePreviousLine width="16px" />
                    Previous
                  </Button>
                  <span>
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    title="next"
                    variant="plain"
                    disabled={!hasNextPage || isFetching}
                    onClick={fetchNextPage}
                  >
                    Next
                    <IconPageNextLine width="16px" />
                  </Button>
                </PagerBar>
                {props.resultList({
                  data: data?.items ?? [],
                  query: debouncedSearchFilter.query ?? "",
                })}
              </VSpace>
            </Main>
            <Footer>
              <Copyright />
            </Footer>
          </LayoutMain>
        </Styled>
      </SkipContentProvider>
    </>
  );
};

export default Browse;
